import styled from 'styled-components';
import { thinScrollbar } from '../chatSurfaces.styled';

/**
 * The parts of the composer nothing else wears. Its bar, field, wrapper and
 * reply strip are shared with the recorder and the search fields, so they live
 * in `chatSurfaces.styled`.
 */

/**
 * The `@` picker.
 *
 * Sits in the composer's normal flow above the bar rather than floating over
 * the conversation: the composer is already absolutely positioned over the
 * thread and publishes its own height, so a popover would either be clipped or
 * need a second measurement to keep the messages clear of it.
 */
export const MentionMenu = styled.div`
  margin-bottom: 8px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  max-height: 180px;
  overflow-y: auto;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.shadow1};

  ${thinScrollbar}
`;

/**
 * One person in the picker: initials, name, role.
 *
 * The role is what makes the list usable rather than decorative - two
 * colleagues share a first name often enough, and "Support" under one of them
 * settles it without opening anything.
 */
export const MentionItem = styled.button<{ $active?: boolean }>`
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 12px;
  font-family: inherit;
  color: ${({ theme }) => theme.darkGray};
  background: ${({ theme, $active }) =>
    $active ? theme.mediumGray : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.mediumGray};
  }
`;

/** The two lines beside the avatar, so a long name truncates rather than wraps. */
export const MentionText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const MentionName = styled.span`
  font-size: 14px;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MentionRole = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.lightGray};
`;

/** "Ana is typing..." - one line, and it must not shift the bar when it goes. */
/**
 * The square inside the send disc while Viktor is writing - the universal
 * "stop", drawn rather than imported, the same as the recorder's. The disc
 * itself is the send button: Stop takes Send's place rather than sitting
 * beside it, because while Viktor is answering there is nothing to send - a
 * new question would interrupt the answer, and the one control that makes
 * sense is calling it off.
 */
export const StopGlyph = styled.span`
  display: block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${({ theme }) => theme.white};

  @media (max-width: 800px) {
    width: 14px;
    height: 14px;
  }
`;

export const TypingLine = styled.div`
  padding: 0 16px 6px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.lightGray};
`;
